<template>
  <transition name="gs-slide">
    <div class="gs-popup" v-if="visible">

      <!-- Header -->
      <div class="gs-header">
        <div class="gs-title-group">
          <span class="gs-title">Get started with BookedSolid</span>
          <span class="gs-subtitle">{{ completedCount }} of {{ steps.length }} complete</span>
        </div>
        <div class="gs-header-right">
          <div class="gs-progress-bar">
            <div class="gs-progress-fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <button class="gs-close" @click="dismiss" title="Dismiss">
            <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
              <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Free trial counter -->
      <div class="gs-trial-bar">
        <svg viewBox="0 0 16 16" fill="none" width="12" height="12" class="gs-trial-icon">
          <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8 4.5v3.8l2.2 1.3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <span>
          <strong>{{ freeTrial }} {{ freeTrial === 1 ? 'day' : 'days' }} left</strong> on your free trial
        </span>
      </div>

      <!-- Steps -->
      <ul class="gs-steps">
        <li
          v-for="step in steps"
          :key="step.id"
          class="gs-step"
          :class="{ completed: step.done, pending: step.pending && !step.done }"
        >
          <div class="gs-step-icon">
            <svg v-if="step.done" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="10" fill="#22c55e"/>
              <path d="M6 10.5l3 3 5-5" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else-if="step.pending" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" stroke="#f59e0b" stroke-width="1.5"/>
              <path d="M10 6v4.5l2.5 1.5" stroke="#f59e0b" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <svg v-else viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" stroke="#d1d5db" stroke-width="1.5"/>
            </svg>
          </div>

          <div class="gs-step-body">
            <div class="gs-step-title" :class="{ strikethrough: step.done }">
              {{ step.title }}
              <span v-if="step.pending && !step.done" class="pending-badge">
                <svg viewBox="0 0 16 16" fill="none" width="11" height="11">
                  <circle cx="8" cy="8" r="7" stroke="#f59e0b" stroke-width="1.5"/>
                  <path d="M8 5v3.5l2 1.2" stroke="#f59e0b" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                Pending
              </span>
            </div>
            <div class="gs-step-desc">{{ step.description }}</div>
          </div>

          <router-link v-if="!step.done && step.link" :to="step.link" class="gs-step-action">
            {{ step.pending ? 'View' : 'Set up' }} →
          </router-link>
          <a v-if="!step.done && step.href" :href="step.href" class="gs-step-action" target="_blank" rel="noopener noreferrer">
            {{ step.pending ? 'View' : 'Set up' }} →
          </a>
        </li>
      </ul>

    </div>
  </transition>
</template>

<script>
import { getAvailabilities, getPhoneNumbers, getClientSettings } from '@/app/admin-routes';

export default {
  name: 'GettingStarted',
  emits: ['dismiss'],
  props: {
    freeTrial: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      visible: false,
      availabilities: null,
      phoneNumbers: [],
    };
  },
  computed: {
    steps() {
      const hasAvailabilities =
        this.availabilities &&
        (
          (this.availabilities.locations && this.availabilities.locations.length > 0) ||
          (this.availabilities.bookables && this.availabilities.bookables.length > 0)
        );

      const hasActiveNumber = this.phoneNumbers.length > 0 && this.phoneNumbers.some(n => n.number && !n.pending);
      const hasPendingNumber = this.phoneNumbers.length > 0 && this.phoneNumbers.some(n => n.pending);
      const numberDone = hasActiveNumber;
      const numberPending = hasPendingNumber && !hasActiveNumber;

      return [
        {
          id: 'pms',
          title: 'Connect your PMS',
          description: "The AI will automatically teach itself about your practitioners, locations and the services you offer from what's in your PMS.",
          done: hasAvailabilities,
          pending: false,
          link: '/clinic',
        },
        {
          id: 'number',
          title: 'Provision a phone number',
          description: 'This is the phone number your AI receptionist will operate from.',
          done: numberDone,
          pending: numberPending,
          link: '/channels',
        },
        {
          id: 'forwarding',
          title: 'Set up call forwarding',
          description: "Divert calls from your clinic's phone to the AI so it can answer your patients' calls.",
          done: false,
          pending: false,
          href: 'https://help.bookedsolid.health/articles/2358473-calls',
        },
      ];
    },
    completedCount() {
      return this.steps.filter(s => s.done).length;
    },
    progressPercent() {
      return Math.round((this.completedCount / this.steps.length) * 100);
    },
    allDone() {
      return this.steps.every(s => s.done);
    },
  },
  methods: {
    dismiss() {
      this.visible = false;
      this.$emit('dismiss');
    },
  },
  async mounted() {
    try {
      const [avail, numbers, settings] = await Promise.all([
        getAvailabilities(),
        getPhoneNumbers(),
        getClientSettings(),
      ]);
      this.availabilities = avail || null;
      this.phoneNumbers = numbers?.phoneNumbers || [];
    } catch (e) {
      console.warn('GettingStarted fetch error', e);
    }
    this.visible = true;
  },
};
</script>

<style scoped lang="scss">
@import "/src/assets/styles/variables.scss";

* {
  text-align: left;
}

.gs-popup {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 340px;
  max-width: calc(100vw - 48px);
  background: white;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  z-index: 1400;
  overflow: hidden;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

// Slide-up entrance animation
.gs-slide-enter-active { transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
.gs-slide-leave-active { transition: all 0.2s ease-in; }
.gs-slide-enter-from  { opacity: 0; transform: translateY(20px) scale(0.97); }
.gs-slide-leave-to   { opacity: 0; transform: translateY(12px); }

.gs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #f3f4f6;

  .gs-title-group {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;

    .gs-title {
      font-size: 13px;
      font-weight: 700;
      color: #111827;
    }

    .gs-subtitle {
      font-size: 11px;
      color: #9ca3af;
    }
  }

  .gs-header-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;

    .gs-progress-bar {
      width: 60px;
      height: 5px;
      background: #e5e7eb;
      border-radius: 99px;
      overflow: hidden;

      .gs-progress-fill {
        height: 100%;
        background: linear-gradient(90deg, $colourP, $colourS);
        border-radius: 99px;
        transition: width 0.4s ease;
      }
    }

    .gs-close {
      background: none;
      border: none;
      padding: 4px;
      color: #9ca3af;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: all 0.15s;
      line-height: 0;

      &:hover {
        color: #374151;
        background: #f3f4f6;
      }
    }
  }
}

.gs-trial-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  background: #eff6ff;
  border-bottom: 1px solid #dbeafe;
  font-size: 11px;
  color: #1d4ed8;

  .gs-trial-icon {
    flex-shrink: 0;
    color: #3b82f6;
  }

  strong { font-weight: 700; }
}

.gs-steps {
  list-style: none;
  margin: 0;
  padding: 6px 0 8px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.gs-step {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
  transition: background 0.12s;

  &:hover { background: #f5f7fb; }
  &.pending { background: #fffbeb; &:hover { background: #fef3c7; } }

  .gs-step-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    svg { width: 20px; height: 20px; display: block; }
  }

  .gs-step-body {
    flex: 1;
    min-width: 0;
    text-align: left;

    .gs-step-title {
      font-size: 12px;
      font-weight: 600;
      color: #111827;
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
      line-height: 1.3;

      &.strikethrough {
        text-decoration: line-through;
        color: #9ca3af;
        font-weight: 400;
      }

      .pending-badge {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        font-size: 10px;
        font-weight: 500;
        color: #92400e;
        background: #fde68a;
        padding: 1px 6px;
        border-radius: 99px;
      }
    }

    .gs-step-desc {
      font-size: 11px;
      color: #9ca3af;
      margin-top: 1px;
      line-height: 1.4;
    }
  }

  .gs-step-action {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
    color: $colourP;
    text-decoration: none;
    white-space: nowrap;
    padding: 3px 9px;
    border-radius: 5px;
    border: 1px solid rgba($colourP, 0.3);
    transition: all 0.12s;

    &:hover {
      background: rgba($colourP, 0.07);
      border-color: $colourP;
    }
  }
}
</style>
